/* import __COLOCATED_TEMPLATE__ from './buyer-application-card.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { htmlEntitiesDecode } from 'sh-www/utils/misc';
import {
  POSTING__APPLICATION_EXPIRATION_STATUSES_ALLOWED,
  POSTING__APPLICATION_EXPIRATION_SHOW_FOR_DAYS,
} from 'sh-www/constants/routes/posting';
import clsx from 'clsx';

/** @type {object} */
const THEMES = {
  BLACK: 'black',
  WHITE: 'white',
};

const CLASS_NAME = 'GLM-c-buyer-application-card';
const CLASS_NAME_MEDIA_HOVERED = `${CLASS_NAME}--media-hovered`;

const THEME_TO_CLASS_MAP = Object.keys(THEMES).reduce((accum, themeKey) => {
  const theme = THEMES[themeKey];
  accum[theme] = `${CLASS_NAME}--theme-${theme}`;
  return accum;
}, {});

class BuyerApplicationCard extends Component {
  @service('commands/application-commands') applicationCommandService;
  @service constants;
  @service media;
  @service moment;
  @service session;

  /** @var {Boolean} mediaIsHovered whether media is being hovered */
  @tracked mediaIsHovered = false;

  /** @var {Boolean} digestThumbnailError whether image failed to load */
  @tracked digestThumbnailError = false;

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
      THEME_TO_CLASS_MAP[this.theme],
      this.mediaIsHovered && CLASS_NAME_MEDIA_HOVERED,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get introTextClassNames() {
    const noMatchingServices = this.serviceMatches.length === 0;

    return clsx(
      `${CLASS_NAME}__intro-text`,
      noMatchingServices && `${CLASS_NAME}__intro-text--no-matching-services`,
    );
  }

  get chipLineClamp() {
    return this.media.isMobile
      ? 2
      : 1;
  }

  get theme() {
    return this.args.theme || THEMES.BLACK;
  }

  get chipThemeMap() {
    return {
      [THEMES.BLACK]: THEMES.WHITE,
      [THEMES.WHITE]: THEMES.BLACK,
    };
  }

  get isShowExpirationWarning() {
    const {
      expiresAt,
      status,
    } = this.args.application;

    if (!expiresAt) {
      return false;
    }

    const showExpirationCutoffDaysFromNow = this.moment.moment().add(POSTING__APPLICATION_EXPIRATION_SHOW_FOR_DAYS, 'd');
    const isWithinReminderTimeLimit = expiresAt.getTime() < showExpirationCutoffDaysFromNow.toDate().getTime();

    return isWithinReminderTimeLimit &&
      POSTING__APPLICATION_EXPIRATION_STATUSES_ALLOWED.includes(status);
  }

  get assignmentExperienceIdMap() {
    return (this.args.assignment.classifications ?? []).reduce((acc, classification) => {
        acc[classification.id] = classification;
        return acc;
    }, {});
  }

  get assignmentServiceIdMap() {
    return (this.args.assignment.services ?? []).reduce((acc, service) => {
        acc[service.id] = service;
        return acc;
    }, {});
  }

  get experienceMatches() {
    const { assignmentExperienceIdMap } = this;
    return this.args.application.seller.experiences.filter(({ id }) => assignmentExperienceIdMap[id]);
  }

  get serviceMatches() {
    const { assignmentServiceIdMap } = this;
    return this.args.application.seller.services.filter(({ id }) => assignmentServiceIdMap[id]);
  }

  get pitchHeadline() {
    return htmlEntitiesDecode(this.args.application.pitch.headline);
  }

  get sellerName() {
    const { seller } = this.args.application;
    const { currentUser } = this.session;

    return currentUser.isBuyer && currentUser.company.isNeedsCreditCard
      ? seller.fullNameSingleLastDigit
      : seller.fullName;
  }

  get shownQuestionResponse() {
    const { assignmentQuestionResponses } = this.args.application;

    return assignmentQuestionResponses.length > 0
      ? assignmentQuestionResponses[0].content
      : '';
  }

  @action
  handleMediaMouseEnter() {
    this.mediaIsHovered = true;
  }

  @action
  handleMediaMouseLeave() {
    this.mediaIsHovered = false;
  }

  @action
  handleDigestImageError() {
    this.digestThumbnailError = true;
  }
}

export default BuyerApplicationCard;
